import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { DialogActions } from "ui/dialog";
import { Select, type OptionType } from "ui/select";
import SelectedTemplates from "components/modals/add-touchpoints-from-template-modal/steps/campaign-template-selection-step/selected-templates";

import { useGetTouchpointContentTypesQuery, usePopulationTypeOptions } from "hooks/api/dictionary";

import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";

import { MondayTouchpointTemplateDTO } from "generated/schemas";

import Icons from "assets";

const FIELDS_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const CampaignTemplateSelectionStep = () => {
  const {
    strategies,
    isLoadingStrategies,
    campaignTemplates,
    isLoadingCampaignTemplates,
    campaignObjective,
    setInvalidTemplateIds,
    usedCampaignTemplateIds,
  } = useAddTouchpointsFromMondayTemplateContext();

  const { control, watch, setValue } = useFormContext();

  const selectedStrategy = watch("strategy");
  const selectedPopulationType = watch("populationType");
  const selectedCampaignTemplate = watch("campaignTemplates");

  const { data: validContentTypes = [] } = useGetTouchpointContentTypesQuery(
    campaignObjective || undefined,
  );
  const { populationTypeOptions } = usePopulationTypeOptions();

  const validContentTypeDescriptions = useMemo(() => {
    return new Set(validContentTypes.map((ct) => ct.description));
  }, [validContentTypes]);

  const getStrategyPlaceholder = (): string => {
    if (isLoadingStrategies) {
      return "Loading strategies...";
    }
    if (!strategies.length) {
      return "No strategies available";
    }
    return "";
  };

  const getCampaignTemplatesPlaceholder = (): string => {
    if (isLoadingCampaignTemplates) {
      return "Loading templates...";
    }
    return "";
  };

  const updateTemplatesForSelectedCampaignTemplates = (
    selectedCampaignTemplateIds: (string | number)[],
    validContentTypes: Set<string>,
  ) => {
    if (selectedCampaignTemplateIds.length) {
      // Get all valid touchpoint template IDs from the newly selected campaign templates
      const validTouchpointTemplateIds = new Set<string>();
      const touchpointTemplatesMap = new Map<string, MondayTouchpointTemplateDTO>();

      selectedCampaignTemplateIds.forEach((campaignTemplateId: string | number) => {
        const campaignTemplate = campaignTemplates.find((ct) => ct.id === campaignTemplateId);

        campaignTemplate?.touchpointTemplates?.forEach((touchpointTemplate) => {
          if (touchpointTemplate.id) {
            validTouchpointTemplateIds.add(touchpointTemplate.id);
            touchpointTemplatesMap.set(touchpointTemplate.id, touchpointTemplate);
          }
        });
      });

      // Get current templates object
      const currentTemplateSelections = watch("templates") || {};

      // Helper function to check if template is valid
      const isContentTypeValid = (touchpointTemplate: MondayTouchpointTemplateDTO): boolean => {
        return (
          !touchpointTemplate.contentType || validContentTypes.has(touchpointTemplate.contentType)
        );
      };

      // Build new templates object: keep existing valid values, add new ones with default true, set invalid to false
      // Also collect invalid template IDs to store in context
      const invalidIds = new Set<string>();

      const updatedTemplates = Array.from(validTouchpointTemplateIds).reduce(
        (templateSelections, touchpointTemplateId) => {
          const currentTouchpointTemplate = touchpointTemplatesMap.get(touchpointTemplateId);
          if (!currentTouchpointTemplate) return templateSelections;

          // Check if template is valid based on content type
          const hasValidContentType = isContentTypeValid(currentTouchpointTemplate);

          // If invalid, always set to false and add to invalid IDs
          if (!hasValidContentType) {
            templateSelections[touchpointTemplateId] = false;
            invalidIds.add(touchpointTemplateId);
          } else {
            // Preserve existing value if it exists, otherwise default to true
            templateSelections[touchpointTemplateId] =
              currentTemplateSelections[touchpointTemplateId] ?? true;
          }

          return templateSelections;
        },
        {} as Record<string, boolean>,
      );

      // Set the updated templates object
      setValue("templates", updatedTemplates, { shouldValidate: false });
      // Store invalid template IDs in context for use in step-2
      setInvalidTemplateIds(invalidIds);
    } else {
      // If no campaign templates selected, clear all template selections
      setValue("templates", {}, { shouldValidate: false });
      // Clear invalid template IDs
      setInvalidTemplateIds(new Set());
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-gray-600">Please choose campaign template from existing templates.</p>
        <Box sx={FIELDS_SX}>
          <Controller
            key="strategy"
            name="strategy"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const strategyOptions = strategies.map((strategy) => ({
                label: strategy.strategy || strategy.id || "",
                value: strategy.id || "",
              }));

              return (
                <Select
                  label={<span className="font-bold">Strategy</span>}
                  selectName="strategy"
                  placeholder={getStrategyPlaceholder()}
                  required
                  disabled={isLoadingStrategies}
                  options={strategyOptions}
                  ariaLabel="Select a strategy"
                  value={strategyOptions.find((option) => option.value === value) || null}
                  onChange={(selectedOption) => {
                    onChange(selectedOption?.value);
                    setValue("populationType", "");
                    setValue("campaignTemplates", []);
                  }}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                  {...field}
                />
              );
            }}
          />
          <Controller
            key="populationType"
            name="populationType"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <Select
                label={<span className="font-bold">Population type</span>}
                selectName="populationType"
                ariaLabel="Select population type"
                required
                disabled={!selectedStrategy}
                helperText="Available after strategy is selected."
                options={populationTypeOptions}
                value={populationTypeOptions.find((option) => option.value === value) ?? null}
                onChange={(selectedOption) => {
                  onChange(selectedOption?.value ?? "");
                  setValue("campaignTemplates", []);
                }}
                {...field}
              />
            )}
          />
          <Controller
            key="campaignTemplates"
            name="campaignTemplates"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const templateOptions: OptionType[] = campaignTemplates.map((template) => ({
                label: usedCampaignTemplateIds.has(template.id || "")
                  ? `${template.campaignTemplate} (already added)`
                  : template.campaignTemplate,
                value: template.id || "",
                isDisabled: usedCampaignTemplateIds.has(template.id || ""),
              }));

              const selectedOptions = Array.isArray(value)
                ? value
                    .map((val) => templateOptions.find((opt) => opt.value === val))
                    .filter((opt) => opt !== undefined)
                : [];

              return (
                <Select
                  label={<span className="font-bold">Campaign templates</span>}
                  selectName="campaignTemplates"
                  placeholder={getCampaignTemplatesPlaceholder()}
                  required
                  multiple
                  disabled={
                    !selectedStrategy || !selectedPopulationType || isLoadingCampaignTemplates
                  }
                  helperText="Available after strategy and population type are selected."
                  options={templateOptions}
                  ariaLabel="Select Campaign Templates"
                  value={selectedOptions}
                  onChange={(selected) => {
                    const values = selected.map((option) => option.value as string);

                    onChange(values);
                    updateTemplatesForSelectedCampaignTemplates(
                      values,
                      validContentTypeDescriptions,
                    );
                  }}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                  {...field}
                />
              );
            }}
          />
          <SelectedTemplates
            selectedCampaignTemplates={selectedCampaignTemplate}
            campaignTemplates={campaignTemplates}
          />
        </Box>
      </div>
    </>
  );
};

export const CampaignTemplateSelectionStepActions = () => {
  const { next, setIsOpen } = useAddTouchpointsFromMondayTemplateContext();
  const { watch } = useFormContext();

  const selectedCampaignTemplate = watch("campaignTemplates");

  return (
    <DialogActions
      type="steps"
      secondary={
        <Button size="large" variant="outlined" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
      }
      primary={
        <Button size="large" disabled={!selectedCampaignTemplate?.length} onClick={next}>
          Continue with templates
        </Button>
      }
    />
  );
};

export default CampaignTemplateSelectionStep;
